import { useAsyncData, useRoute, useRuntimeConfig } from '#imports'
import { type Ref, computed, onUnmounted, ref, watchEffect } from 'vue'

import { mapToReviews } from '@backmarket/nuxt-layer-cms/Reviews.utils'
import type { Review } from '@backmarket/nuxt-layer-cms/ReviewsView.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import type { GalleryImage, GalleryImageExtraItem } from '../GalleryNew.types'
import { getMatchingReviewId } from '../components/Review/Review.utils'
import {
  YOUTUBE_VIDEO_THUMBNAIL_URL,
  YOUTUBE_VIDEO_TITLE,
} from '../components/YoutubeVideo/YoutubeVideo.constants'

/**
 * Make sure the ideal index is different
 * in each extra item type.
 *
 * `idealIndex` is the 0-indexed position of
 * the extra item in the carousel.
 */
export const EXTRA_ITEMS_CONFIGURATION = Object.freeze({
  review: {
    idealIndex: 4,
  },
  video: {
    idealIndex: 3,
  },
})

/**
 * Helper function to encapsulate the logic for extra items in the gallery.
 * Each item type will have a different logic: different source and positionning logic.
 */
export function useExtraItems({
  categoryId,
  productBrand,
  productModel,
  baseCarouselImages,
}: {
  categoryId: string
  productBrand: string
  productModel: string
  baseCarouselImages: Ref<GalleryImage[]>
}) {
  const REVIEW_PLACEHOLDER_ID = 'extra:review'
  const extraItems = ref<GalleryImageExtraItem[]>([])

  type ReviewWithCount = Review & { count: number }

  const currentReview = ref<ReviewWithCount | null>(null)

  const experiments = useExperiments()
  const marketplace = useMarketplace()
  const config = useRuntimeConfig()

  const route = useRoute()

  const $httpFetch = useHttpFetch()

  // REVIEWS
  const isReviewsInCarouselEnabled = computed(() => {
    const isEnabledFF =
      experiments['experiment.ppReviewsInCarousel'] === 'withReviewsInCarousel'

    const isEnabledQueryParam =
      route.query.reviewsInCarousel === 'true' ||
      route.query.reviewsInCarousel === '1'

    return isEnabledFF || isEnabledQueryParam
  })

  const reviewIds = computed(() => {
    // Do not gate by feature flag here; caller decides visibility.
    if (!categoryId) return null

    const matchingReviewIds = getMatchingReviewId({
      countryCode: marketplace.market.countryCode,
      categoryId,
      productBrand,
      productModel,
    })

    return matchingReviewIds
  })

  // VIDEOS
  const isVideosInCarouselEnabled = computed(() => {
    // 100% of users in the US, handled by the kill switch
    const isEnabledFF = config.public.FF_KILL_VIDEOS_IN_NEW_CAROUSEL === false

    return isEnabledFF
  })

  const videos = computed(() => {
    if (isVideosInCarouselEnabled.value) {
      return [
        {
          id: `extra:video-${YOUTUBE_VIDEO_THUMBNAIL_URL}`,
          src: YOUTUBE_VIDEO_THUMBNAIL_URL,
          alt: YOUTUBE_VIDEO_TITLE,
          isExtra: true,
          type: 'video',
          desktopOrder: 0,
        } as const satisfies GalleryImageExtraItem,
      ]
    }

    return null
  })

  const isExtra = (item: GalleryImage) => {
    return 'isExtra' in item && item.isExtra
  }

  const isVideo = (item: GalleryImage) => {
    return isExtra(item) && item.type === 'video'
  }

  const isReview = (item: GalleryImage) => {
    return isExtra(item) && item.type === 'review'
  }

  function injectExtraItemsAtPosition(
    items: Ref<GalleryImage[]>,
  ): GalleryImage[] {
    if (!items || !items.value) {
      return []
    }

    if (extraItems.value.length === 0) {
      return items.value
    }

    const review = isReviewsInCarouselEnabled.value
      ? extraItems.value.find((item) => item.type === 'review')
      : null
    const video = isVideosInCarouselEnabled.value
      ? extraItems.value.find((item) => item.type === 'video')
      : null

    const result = [...items.value]
    const extraItemsToAdd = [] as GalleryImageExtraItem[]

    if (video) extraItemsToAdd.push(video)
    if (review) extraItemsToAdd.push(review)

    extraItemsToAdd.forEach((item) => {
      const itemsConfig = EXTRA_ITEMS_CONFIGURATION[item.type]
      // The min between the ideal index and the length of the result
      // This way we ensure that the extra items are added at the correct index
      // or at the end of the result if the ideal index is not reached
      const idealIndex = Math.min(itemsConfig.idealIndex, result.length)

      if (idealIndex <= result.length) {
        result.splice(idealIndex, 0, item)
      } else {
        result.push(item)
      }
    })

    return result
  }

  const imagesListWithExtraItems = ref<GalleryImage[]>([])

  const extraItemsIndexes = computed(() => {
    return imagesListWithExtraItems.value.length >
      baseCarouselImages.value.length
      ? imagesListWithExtraItems.value.reduce((acc, image, index) => {
          if (isExtra(image)) {
            acc.push(index)
          }

          return acc
        }, [] as number[])
      : []
  })

  const extraItemsByType = computed(() => {
    const result = { video: -1, review: -1 }

    imagesListWithExtraItems.value.forEach((item, index) => {
      if (isVideo(item)) {
        result.video = index
      } else if (isReview(item)) {
        result.review = index
      }
    })

    return result
  })

  const areThereAnyVideos = computed(() => {
    return extraItemsByType.value.video !== -1
  })
  const areThereAnyReviews = computed(() => {
    return extraItemsByType.value.review !== -1
  })

  /**
   * This function is used to get the desktop flex-order of an item based
   * its type and the presence of videos and reviews.
   *
   * To prevent hydration mismatches, we calculate the order based on the
   * enabled features rather than the actual presence of items in the list.
   */
  const getDesktopOrder = (
    item: GalleryImage,
    index: number,
    hasVideos: boolean,
    hasReviews: boolean,
  ): number => {
    const isRegular = !isExtra(item)

    // Define order rules as a map for better readability
    // These rules match the original CSS md:order-* classes exactly
    const orderRules = [
      // md:order-1: video (or review if no videos)
      { condition: isVideo(item), order: 1 },
      { condition: isReview(item), order: 1 },

      // md:order-2:
      { condition: index === 1 && hasVideos, order: 2 },
      { condition: index === 3 && !hasVideos && hasReviews, order: 2 },
      { condition: index === 2 && hasVideos && hasReviews, order: 2 },

      // md:order-3:
      { condition: index === 2 && hasVideos && !hasReviews, order: 3 },

      // md:order-4:
      {
        condition: index > 3 && hasVideos && isRegular,
        order: 4 + (index - 4),
      },
    ]

    // Find the first matching rule
    const matchingRule = orderRules.find(({ condition }) => condition)

    // fallback to current position
    return matchingRule?.order ?? index
  }

  watchEffect(() => {
    // Clear the array to prevent duplicates
    extraItems.value = []

    if (videos.value) {
      extraItems.value.push({
        ...videos.value[0],
      })
    }

    const hasReviews = Boolean(reviewIds.value?.length)

    // Push a review right away, for skeleton loading
    if (hasReviews) {
      extraItems.value.push({
        id: REVIEW_PLACEHOLDER_ID,
        // placeholder image
        src: '',
        alt: '',
        isExtra: true,
        type: 'review',
        desktopOrder: 0,
      })
    }

    // Build a list of images with placeholders
    imagesListWithExtraItems.value =
      injectExtraItemsAtPosition(baseCarouselImages)

    const hasVideos = Boolean(isVideosInCarouselEnabled.value)

    imagesListWithExtraItems.value = imagesListWithExtraItems.value.map(
      (item, index) => ({
        ...item,
        desktopOrder: getDesktopOrder(item, index, hasVideos, hasReviews),
      }),
    )

    // Fire async fetch only if we expect reviews
    if (hasReviews) {
      const { data: review } = useAsyncData(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        `review-in-carousel-${reviewIds.value!.join(',')}`,
        () =>
          $httpFetch('/reviews/v1/products/reviews', {
            query: { public_id__in: reviewIds.value ?? undefined },
          }),
        {
          transform: (res) => {
            const mappedReviews = mapToReviews(res.results)

            // returns only the first review that has pictures
            return mappedReviews[0]
              ? { ...mappedReviews[0], count: res.count }
              : null
          },
          getCachedData(key, nuxtApp) {
            return nuxtApp.payload.data[key]
          },
          immediate: true,
          server: true,
        },
      )

      if (review?.value) {
        currentReview.value = review.value
      }

      if (review?.value?.pictures[0]) {
        // Update the placeholder item in-place; keep position,
        // but update the src, alt and id
        const idx = imagesListWithExtraItems.value.findIndex(
          (i) => i.id === REVIEW_PLACEHOLDER_ID,
        )
        if (idx !== -1) {
          imagesListWithExtraItems.value[idx] = {
            ...imagesListWithExtraItems.value[idx],
            src: review.value.pictures[0],
            alt:
              review.value.product.title ||
              review.value.product.model ||
              review.value.customer.firstName ||
              '',
            id: `extra:review-${review.value.id}`,
          }
        }
      }
    }
  })

  onUnmounted(() => {
    currentReview.value = null
  })

  return {
    areThereAnyVideos,
    areThereAnyReviews,
    extraItemsIndexes,
    imagesListWithExtraItems,
    injectExtraItemsAtPosition,
    currentReview,
  }
}
